import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, tap } from 'rxjs';
import { RelatedOperatingDataApiService } from '@shared/services';
import { AvailableSignalDTO, MeasurementSignalDTO, RelatedOpDataResponseDTO } from '@shared/models';
import { DateHelper } from '@shared/helpers';
import { LoadOperatingSignalsParams, OperatingSignalsLoadType, OperatingSignalsStateModel } from './operating-signals.model';

const PREDEFINED_SIGNAL_IDS: readonly number[] = [1, 2, 4, 5, 6];

interface ParsedOperatingSignalsResponse {
  freshSeries: MeasurementSignalDTO[];
  availableSignals: AvailableSignalDTO[];
  lastSignalReceived: string | null;
}

const createDefaultState = (): OperatingSignalsStateModel => ({
  seriesData: [],
  availableSignals: [],
  selectedSignalTypeIds: [...PREDEFINED_SIGNAL_IDS],
  predefinedSignalTypeIds: [...PREDEFINED_SIGNAL_IDS],
  currentMachineId: null,
  currentDateHours: null,
  isLoading: true,
  error: null,
});

const toSeriesKey = (item: { typeId: number; component: string }): string => `${item.typeId}::${item.component}`;

const groupSeriesByComponent = (series: MeasurementSignalDTO[]): { [component: string]: MeasurementSignalDTO[] } =>
  series.reduce<{ [component: string]: MeasurementSignalDTO[] }>((grouped, item) => {
    const component = item.component || 'Unknown';
    grouped[component] = [...(grouped[component] ?? []), item];
    return grouped;
  }, {});

const parseOperatingSignalsResponse = (
  response: RelatedOpDataResponseDTO | RelatedOpDataResponseDTO[] | null
): ParsedOperatingSignalsResponse => {
  const blocks: RelatedOpDataResponseDTO[] = Array.isArray(response) ? response : response ? [response] : [];
  const freshSeries: MeasurementSignalDTO[] = [];
  const availableSignalsByKey = new Map<string, AvailableSignalDTO>();
  const receivedTimestamps: string[] = [];

  for (const block of blocks) {
    if (!block) {
      continue;
    }

    freshSeries.push(...(block.seriesData ?? []));

    for (const availableSignal of block.availableSignals ?? []) {
      availableSignalsByKey.set(toSeriesKey(availableSignal), availableSignal);
    }

    if (block.lastSignalReceived) {
      receivedTimestamps.push(block.lastSignalReceived);
    }
  }

  const sortedTimestamps = [...receivedTimestamps].sort();

  return {
    freshSeries,
    availableSignals: Array.from(availableSignalsByKey.values()),
    lastSignalReceived: sortedTimestamps.length > 0 ? (sortedTimestamps[sortedTimestamps.length - 1] ?? null) : null,
  };
};

@Injectable({ providedIn: 'root' })
export class OperatingSignalsStore {
  private readonly apiService = inject(RelatedOperatingDataApiService);
  private readonly state = signal<OperatingSignalsStateModel>(createDefaultState());

  readonly isLoading = computed(() => this.state().isLoading);
  readonly error = computed(() => this.state().error);
  readonly availableSignals = computed(() => this.state().availableSignals);
  readonly selectedSignalTypeIds = computed(() => this.state().selectedSignalTypeIds);
  readonly seriesData = computed(() => this.state().seriesData ?? []);
  readonly displayedSeriesData = computed(() => {
    const current = this.state();
    return current.selectedSignalTypeIds.length === 0 ? [] : current.seriesData;
  });
  readonly currentMachineId = computed(() => this.state().currentMachineId);
  readonly currentDateHours = computed(() => this.state().currentDateHours);
  readonly lastSignalReceived = computed(() => this.state().lastSignalReceived);

  load(params: LoadOperatingSignalsParams): void {
    this.patch({ isLoading: true, error: null });

    const { machineId, loadType } = params;
    const previous = this.state();
    const signalTypeIds = params.signalTypeIds ?? [...previous.predefinedSignalTypeIds];
    const dateHours = params.dateHours ?? previous.currentDateHours;
    const dateRange = DateHelper.hoursBack(dateHours ?? undefined);

    this.apiService
      .getSignalDataAndAvailability(machineId, signalTypeIds, dateRange)
      .pipe(
        tap((response) => {
          const parsed = parseOperatingSignalsResponse(response);

          if (loadType === OperatingSignalsLoadType.Initial) {
            this.applyInitialLoad(parsed, previous, machineId, dateHours);
          } else {
            this.applyAdditionalLoad(parsed, previous);
          }
        }),
        catchError((error: Error | undefined) => {
          this.patch({ isLoading: false, error: error?.message ?? 'Failed to load operating signals' });
          return EMPTY;
        })
      )
      .subscribe();
  }

  toggleSignalVisibility(typeId: number): void {
    const previous = this.state();
    const selectedSignalTypeIds = [...previous.selectedSignalTypeIds];
    const existingIndex = selectedSignalTypeIds.indexOf(typeId);

    if (existingIndex > -1) {
      selectedSignalTypeIds.splice(existingIndex, 1);
      this.patch({ selectedSignalTypeIds });
      return;
    }

    selectedSignalTypeIds.push(typeId);
    this.patch({ selectedSignalTypeIds });

    const hasSeriesData = (previous.seriesData ?? []).some((series) => series.typeId === typeId);

    if (!hasSeriesData && previous.currentMachineId && !previous.isLoading) {
      this.load({
        machineId: previous.currentMachineId,
        signalTypeIds: [...new Set([...previous.predefinedSignalTypeIds, ...previous.selectedSignalTypeIds, typeId])],
        loadType: OperatingSignalsLoadType.Additional,
      });
    }
  }

  clear(): void {
    this.state.set(createDefaultState());
  }

  setError(error: string | null): void {
    this.patch({ error, isLoading: false });
  }

  private applyInitialLoad(
    parsed: ParsedOperatingSignalsResponse,
    previous: OperatingSignalsStateModel,
    machineId: string,
    dateHours: number | null
  ): void {
    const availableTypeIds = new Set(parsed.freshSeries.map((series) => series.typeId));

    this.patch({
      seriesData: parsed.freshSeries,
      seriesDataByComponent: groupSeriesByComponent(parsed.freshSeries),
      availableSignals: parsed.availableSignals,
      selectedSignalTypeIds: previous.predefinedSignalTypeIds.filter((typeId) => availableTypeIds.has(typeId)),
      currentMachineId: machineId,
      currentDateHours: dateHours,
      lastSignalReceived: parsed.lastSignalReceived,
      isLoading: false,
      error: null,
    });
  }

  private applyAdditionalLoad(parsed: ParsedOperatingSignalsResponse, previous: OperatingSignalsStateModel): void {
    const mergedSeriesByKey = new Map<string, MeasurementSignalDTO>();

    for (const series of previous.seriesData ?? []) {
      mergedSeriesByKey.set(toSeriesKey(series), series);
    }

    for (const series of parsed.freshSeries) {
      mergedSeriesByKey.set(toSeriesKey(series), series);
    }

    this.patch({
      seriesData: Array.from(mergedSeriesByKey.values()),
      seriesDataByComponent: { ...(previous.seriesDataByComponent ?? {}), ...groupSeriesByComponent(parsed.freshSeries) },
      availableSignals: parsed.availableSignals,
      lastSignalReceived: parsed.lastSignalReceived,
      isLoading: false,
      error: null,
    });
  }

  private patch(partial: Partial<OperatingSignalsStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
